import { cn } from "~/core/generic/shadcn-utils";

import Icon from "../Icon";
import { TagProps } from "../Tags/interfaces";

import { StyledTag } from "./styled/styled-tag";

/**
 * Tag component for displaying a label with optional icon and color.
 *
 * This component renders a styled tag that can be customized with different colors,
 * an optional icon, and rounded corners. It also supports a disabled state.
 *
 * @component
 * @param text - The text to display inside the tag.
 * @param color - The color key for the tag, corresponding to the available colors in TAG_COLORS_MAP.
 * @param icon - Optional. The name of the icon to display on the left side of the tag.
 * @param rounded - Optional. If true, the tag will have fully rounded corners (default: false).
 * @param disabled - Optional. If true, the tag will appear disabled and not respond to hover/focus (default: false).
 *
 * @example
 * // Basic usage
 * <Tag text="Active" color="blue" />
 *
 * @example
 * // With an icon and rounded corners
 * <Tag text="Info" color="blue" icon="Sparkle2_12_Outline" rounded />
 *
 * @example
 * // Disabled tag
 * <Tag text="Disabled" color="blue" disabled />
 */

const Tag = ({
  text,
  color,
  icon,
  rounded = false,
  disabled = false,
  isHoverable = false,
  maxWidth,
}: TagProps) => {
  return (
    <StyledTag
      $isHoverable={isHoverable}
      $color={color}
      $disabled={disabled}
      className={cn(
        "h-layout-spacing-13 rounded-radius-5 flex items-center justify-center py-0 px-layout-spacing-5 gap-layout-spacing-7 cursor-pointer",
        {
          "!rounded-full !px-layout-spacing-6 !gap-layout-spacing-6": rounded,
        },
      )}
    >
      {icon && <Icon name={icon} />}
      <span
        className="octolane-ds-tag-text text-size-2 font-sans font-medium leading-height-2 tracking-spacing-2 truncate"
        style={{ maxWidth: maxWidth ?? "100%" }}
      >
        {text}
      </span>
    </StyledTag>
  );
};

Tag.displayName = "Tag";

export default Tag;
